/**
 * SnapFig GA4 Tracking Initialization
 * Initializes event listeners for tracking user interactions
 */

(function() {
  'use strict';

  var pendingCartIntent = null;
  var pendingCartTimer = null;
  var lastBuyNowAt = 0;
  var cartBeforeChange = null;
  var lastKnownCart = null;
  var trackedCartViews = new WeakSet();
  var commerceTrackingBound = false;
  var performanceTrackingStarted = false;

  function getCurrency() {
    return window.Shopify?.currency?.active || 'USD';
  }

  function moneyAmount(cents) {
    return Number(cents || 0) / 100;
  }

  function cartItemToGa(item, quantityOverride) {
    var quantity = quantityOverride == null ? Number(item.quantity || 1) : Number(quantityOverride);
    return {
      item_id: String(item.variant_id || item.id || ''),
      item_name: String(item.product_title || item.title || ''),
      item_variant: String(item.variant_title || ''),
      price: moneyAmount(item.final_price ?? item.price),
      quantity: quantity
    };
  }

  function cartToGa(cart) {
    return {
      currency: cart.currency || getCurrency(),
      value: moneyAmount(cart.total_price),
      items: (cart.items || []).map(function(item) { return cartItemToGa(item); }),
      flow_id: GtagUtils.getFlowId()
    };
  }

  async function fetchCart() {
    var response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error('cart_read_failed');
    return response.json();
  }

  function formIntent(form, source) {
    var formData = new FormData(form);
    var quantity = Math.max(1, Number(formData.get('quantity') || 1));
    var variantId = String(formData.get('id') || '');
    var productInfo = GtagUtils.getProductInfo();
    return {
      flow_id: GtagUtils.getFlowId(),
      checkout_source: source,
      product_id: productInfo.product_id || form.dataset.productId || '',
      variant_id: variantId,
      quantity: quantity,
      generation_model: GtagUtils.getFlowContext().generation_model || '',
      currency: getCurrency()
    };
  }

  function trackCartIntent(form, source, eventName) {
    if (!form || !GtagUtils.shouldTrack(eventName + '_' + source + '_' + String(form.id || 'form'), 500)) return;
    pendingCartIntent = formIntent(form, source);
    GtagUtils.setFlowContext({ checkout_source: source });
    GtagUtils.sendEvent(eventName, pendingCartIntent);
    clearTimeout(pendingCartTimer);
    if (eventName === 'add_to_cart_click') {
      pendingCartTimer = setTimeout(function() {
        if (!pendingCartIntent) return;
        GtagUtils.sendEvent('add_to_cart_error', Object.assign({}, pendingCartIntent, {
          error_type: 'timeout',
          error_code: 'cart_add_timeout',
          retryable: true
        }));
        pendingCartIntent = null;
      }, 15000);
    }
  }

  var productIdsByHandle = {};

  async function getVariantProductId(eventTarget, variant) {
    if (variant.product_id) return String(variant.product_id);
    if (variant.product?.id) return String(variant.product.id);
    if (eventTarget?.dataset?.productId) return String(eventTarget.dataset.productId);
    var formId = String(eventTarget?.id || '');
    var idMatch = formId.match(/-(\d{10,})$/);
    if (idMatch) return idMatch[1];

    var variantPicker = formId
      ? document.querySelector('product-variants[form-id="' + CSS.escape(formId) + '"]')
      : null;
    var productHandle = variantPicker?.getAttribute('handle') || '';
    if (!productHandle && window.location.pathname.includes('/products/')) {
      var handleMatch = window.location.pathname.match(/\/products\/([^/?]+)/);
      productHandle = handleMatch ? handleMatch[1] : '';
    }
    if (!productHandle) return '';
    if (productIdsByHandle[productHandle]) return productIdsByHandle[productHandle];

    try {
      var response = await fetch('/products/' + encodeURIComponent(productHandle) + '.js');
      if (!response.ok) return '';
      var product = await response.json();
      var productId = String(product.id || '');
      if (productId) productIdsByHandle[productHandle] = productId;
      return productId;
    } catch (e) {
      return '';
    }
  }

  function bindCommerceTracking() {
    if (commerceTrackingBound) return;
    commerceTrackingBound = true;
    fetchCart().then(function(cart) { lastKnownCart = cart; }).catch(function() {});
    document.addEventListener('variant:changed', async function(event) {
      var variant = event.detail?.variant;
      if (!variant?.id) return;
      var targetKey = event.target?.id || event.target?.tagName || 'product_form';
      if (!GtagUtils.shouldTrack('select_variant_' + targetKey + '_' + variant.id, 500)) return;
      var productId = await getVariantProductId(event.target, variant);
      GtagUtils.sendEvent('select_variant', {
        product_id: productId,
        variant_id: String(variant.id),
        price: moneyAmount(variant.price)
      });
    });

    document.addEventListener('submit', function(event) {
      var form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.matches('form[action*="/cart/add"], form[is="product-form"]')) {
        if (Date.now() - lastBuyNowAt < 1000) return;
        trackCartIntent(form, 'product_form', 'add_to_cart_click');
      }
    }, true);

    document.addEventListener('click', function(event) {
      var checkoutButton = event.target.closest('button[checkout], .shopify-payment-button, .shopify-payment-button__button');
      if (!checkoutButton) return;
      var form = checkoutButton.closest('form') || document.querySelector('form[is="product-form"]');
      lastBuyNowAt = Date.now();
      trackCartIntent(form, 'buy_now', 'buy_now_click');
    }, true);

    document.addEventListener('variant:added', function(event) {
      var item = event.detail?.variant;
      if (!item) return;
      var intent = pendingCartIntent || {
        flow_id: GtagUtils.getFlowId(),
        checkout_source: 'product_form'
      };
      var gaItem = cartItemToGa(item, intent.quantity);
      GtagUtils.trackEcommerce('add_shopping_cart', {
        currency: intent.currency || getCurrency(),
        value: gaItem.price * gaItem.quantity,
        items: [gaItem],
        flow_id: intent.flow_id,
        checkout_source: intent.checkout_source
      });
      clearTimeout(pendingCartTimer);
      pendingCartIntent = null;
    });

    document.addEventListener('cart-notification:show', function(event) {
      if (event.detail?.status !== 'error' || !pendingCartIntent) return;
      GtagUtils.sendEvent('add_to_cart_error', Object.assign({}, pendingCartIntent, {
        error_type: 'cart_add',
        error_code: 'cart_add_failed',
        retryable: true
      }));
      clearTimeout(pendingCartTimer);
      pendingCartIntent = null;
    });

    document.addEventListener('line-item-quantity:change:start', function(event) {
      if (Number(event.detail?.newLineQuantity) !== 0) return;
      cartBeforeChange = lastKnownCart;
    }, true);

    document.documentElement.addEventListener('cart:updated', function(event) {
      var currentCart = event.detail?.cart;
      if (!currentCart) return;
      var previousCart = cartBeforeChange;
      lastKnownCart = currentCart;
      if (!previousCart) return;
      var currentByKey = Object.fromEntries((currentCart.items || []).map(function(item) {
        return [item.key, Number(item.quantity || 0)];
      }));
      var removedItems = [];
      var removedValue = 0;
      (previousCart.items || []).forEach(function(item) {
        var removedQuantity = Number(item.quantity || 0) - Number(currentByKey[item.key] || 0);
        if (removedQuantity <= 0) return;
        var gaItem = cartItemToGa(item, removedQuantity);
        removedItems.push(gaItem);
        removedValue += gaItem.price * removedQuantity;
      });
      if (removedItems.length) {
        GtagUtils.trackEcommerce('remove_shipping_cart', {
          currency: currentCart.currency || getCurrency(),
          value: removedValue,
          items: removedItems,
          flow_id: GtagUtils.getFlowId()
        });
      }
      cartBeforeChange = null;
    });

    function trackVisibleCart(container) {
      if (!container || trackedCartViews.has(container)) return;
      fetchCart().then(function(cart) {
        if (!cart.item_count) return;
        trackedCartViews.add(container);
        GtagUtils.trackEcommerce('view_cart', cartToGa(cart));
      }).catch(function() {});
    }

    if (window.location.pathname === '/cart') trackVisibleCart(document.body);
    var cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      new MutationObserver(function() {
        if (cartDrawer.hasAttribute('open')) trackVisibleCart(cartDrawer);
        else trackedCartViews.delete(cartDrawer);
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
    }
  }

  function getNavigationInfo() {
    var navigation = performance.getEntriesByType('navigation')[0];
    return {
      navigation_type: navigation?.type || 'navigate',
      is_bfcache: navigation?.type === 'back_forward'
    };
  }

  function metricRating(name, value) {
    var thresholds = { LCP: [2500, 4000], CLS: [0.1, 0.25], INP: [200, 500] };
    var limits = thresholds[name] || [0, 0];
    return value <= limits[0] ? 'good' : value <= limits[1] ? 'needs_improvement' : 'poor';
  }

  function initPerformanceTracking() {
    if (performanceTrackingStarted) return true;
    if (!GtagUtils.isAvailable()) return;
    performanceTrackingStarted = true;
    var bootstrap = window.__snapfigPerformanceBootstrap || {};
    var fallbackNavigationInfo = getNavigationInfo();
    var navigationInfo = {
      navigation_type: bootstrap.navigation_type || fallbackNavigationInfo.navigation_type,
      is_bfcache: typeof bootstrap.is_bfcache === 'boolean'
        ? bootstrap.is_bfcache
        : fallbackNavigationInfo.is_bfcache
    };
    var pageViewId = bootstrap.page_view_id || ('pv_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8));
    var siteBrand = 'snapfig';
    var pageStart = bootstrap.page_start || performance.timeOrigin || Date.now();
    var homeReady = Boolean(bootstrap.home_ready);
    var lastReadyTarget = bootstrap.last_ready_target || 'hero';
    var metricValues = {};

    function sendMetric(name, value) {
      if (metricValues[name]?.sent || !Number.isFinite(value)) return;
      metricValues[name] = { value: value, sent: true };
      GtagUtils.sendEvent('web_vitals', Object.assign({
        page_view_id: pageViewId,
        metric_name: name,
        metric_value: name === 'CLS' ? Number(value.toFixed(4)) : Math.round(value),
        metric_rating: metricRating(name, value),
        site_brand: siteBrand
      }, navigationInfo));
    }

    function flushWebVitals() {
      ['LCP', 'CLS', 'INP'].forEach(function(name) {
        if (metricValues[name]) sendMetric(name, metricValues[name].value);
      });
    }

    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) { metricValues.LCP = { value: entry.startTime }; });
        }).observe({ type: 'largest-contentful-paint', buffered: true });
        var clsValue = 0;
        new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) { if (!entry.hadRecentInput) clsValue += entry.value; });
          metricValues.CLS = { value: clsValue };
        }).observe({ type: 'layout-shift', buffered: true });
        new PerformanceObserver(function(list) {
          list.getEntries().forEach(function(entry) {
            var duration = entry.duration || 0;
            if (!metricValues.INP || duration > metricValues.INP.value) metricValues.INP = { value: duration };
          });
        }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
      } catch (e) {}
    }

    if (window.location.pathname === '/') {
      if (!bootstrap.home_view_sent) {
        bootstrap.home_view_sent = true;
        GtagUtils.sendEvent('home_view_start', Object.assign({
          page_view_id: pageViewId,
          site_brand: siteBrand,
          entry_source: GtagUtils.getEntrySource()
        }, navigationInfo));
      }

      function trackHomeReady() {
        var hero = document.querySelector('.shopify-section--slideshow, .slideshow, [id*="slideshow"]');
        var cta = hero?.querySelector('a, button');
        var productModule = document.querySelector('.shopify-section--featured-products, [id*="featured_product"], [id*="featured-collections"]');
        lastReadyTarget = !hero ? 'hero_missing' : !cta ? 'cta_missing' : !productModule ? 'product_module_missing' : 'first_screen';
        bootstrap.last_ready_target = lastReadyTarget;
        var criticalImages = Array.from(document.querySelectorAll(
          '.shopify-section--slideshow img, .slideshow img, [id*="slideshow"] img, [id*="featured_product"] img, [id*="featured-collections"] img'
        )).filter(function(image) {
          var rect = image.getBoundingClientRect();
          return rect.top < window.innerHeight && rect.bottom > 0;
        }).slice(0, 6);
        var imagePromises = criticalImages.map(function(image) {
          if (image.complete) return Promise.resolve();
          return new Promise(function(resolve) {
            image.addEventListener('load', resolve, { once: true });
            image.addEventListener('error', resolve, { once: true });
          });
        });
        var readiness = Promise.allSettled(imagePromises);
        var timeout = new Promise(function(resolve) { setTimeout(resolve, 10000); });
        Promise.race([readiness, timeout]).then(function() {
          requestAnimationFrame(function() {
            requestAnimationFrame(function() {
            homeReady = true;
            bootstrap.home_ready = true;
            GtagUtils.sendEvent('home_first_screen_ready', Object.assign({
              page_view_id: pageViewId,
              site_brand: siteBrand,
              home_ready_ms: Math.max(0, Math.round(performance.now())),
              ready_target: lastReadyTarget,
              entry_source: GtagUtils.getEntrySource()
            }, navigationInfo));
            });
          });
        });
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', trackHomeReady, { once: true });
      } else {
        trackHomeReady();
      }
    }

    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') {
        flushWebVitals();
      }
    });

    window.addEventListener('pagehide', function() {
      if (window.location.pathname === '/' && !homeReady && !bootstrap.home_abandon_sent) {
        bootstrap.home_abandon_sent = true;
        GtagUtils.sendEvent('home_load_abandon', Object.assign({
          page_view_id: pageViewId,
          site_brand: siteBrand,
          elapsed_ms: Math.max(0, Math.round(Date.now() - pageStart)),
          last_ready_target: lastReadyTarget,
          event_transport: 'beacon'
        }, navigationInfo));
      }
      flushWebVitals();
    });
    return true;
  }

  function initPerformanceWhenReady(attempt) {
    if (initPerformanceTracking()) return;
    if (attempt >= 100) return;
    setTimeout(function() { initPerformanceWhenReady(attempt + 1); }, 100);
  }

  // Wait for DOM to be ready
  function init() {
    if (typeof GtagUtils === 'undefined') {
      console.warn('[GtagTracking] GtagUtils not available, retrying...');
      setTimeout(init, 100);
      return;
    }

    console.log('[GtagTracking] Initializing SnapFig GA tracking...');
    bindCommerceTracking();
    initPerformanceWhenReady(0);
  }

  /**
   * Get tracking data from session storage
   */
  function getTrackingDataFromStorage() {
    try {
      var data = sessionStorage.getItem('snapfig_tracking_data');
      return data ? JSON.parse(data) : {};
    } catch (e) {
      return {};
    }
  }

  /**
   * Save tracking data to session storage (call this during the flow)
   */
  function saveTrackingData(data) {
    try {
      var existing = getTrackingDataFromStorage();
      var merged = Object.assign({}, existing, data);
      sessionStorage.setItem('snapfig_tracking_data', JSON.stringify(merged));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Increment upload count in tracking data
   */
  function incrementUploadCount() {
    var data = getTrackingDataFromStorage();
    data.uploadsBeforeOrder = (data.uploadsBeforeOrder || 0) + 1;
    saveTrackingData(data);
  }

  /**
   * Increment generation count in tracking data
   */
  function incrementGenerationCount(imageCount, modelName) {
    var data = getTrackingDataFromStorage();
    data.generationsBeforeOrder = (data.generationsBeforeOrder || 0) + 1;
    data.imagesGeneratedBeforeOrder = (data.imagesGeneratedBeforeOrder || 0) + (imageCount || 0);
    if (modelName) {
      data.modelName = modelName;
    }
    saveTrackingData(data);
  }

  // Expose helper functions for other scripts to use
  window.GtagTrackingHelpers = {
    saveTrackingData: saveTrackingData,
    incrementUploadCount: incrementUploadCount,
    incrementGenerationCount: incrementGenerationCount,
    getTrackingDataFromStorage: getTrackingDataFromStorage
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
